/* ==========================================================================
   Spring Rate Converter
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-src]');
  if (!root) return;

  var NMM_PER_LBIN = 0.1751268;
  var KGMM_PER_NMM = 1 / 9.80665;
  var G_IN_PER_S2  = 386.089;   /* gravity in inches per second squared */

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function toLbIn(v, unit) {
    if (unit === 'nmm')  return v / NMM_PER_LBIN;
    if (unit === 'kgmm') return (v / KGMM_PER_NMM) / NMM_PER_LBIN;
    return v;
  }

  function compute(input) {
    if (!isFinite(input.value) || input.value <= 0) {
      return { ok: false, errors: ['Enter a spring rate.'] };
    }

    var lbin = toLbIn(input.value, input.unit);
    var nmm  = lbin * NMM_PER_LBIN;
    var kgmm = nmm * KGMM_PER_NMM;

    var mr = isFinite(input.motion) && input.motion > 0 ? input.motion : 1;
    var wheelRate = lbin * mr * mr;

    var figures = [
      { label: 'Pounds per inch', value: num(lbin, 1) + ' lb/in' },
      { label: 'Newtons per millimetre', value: num(nmm, 2) + ' N/mm' }
    ];

    var rows = [
      { head: 'All units' },
      { label: 'lb/in', value: num(lbin, 1) },
      { label: 'N/mm', value: num(nmm, 2) },
      { label: 'kg/mm', value: num(kgmm, 2) }
    ];

    if (mr !== 1) {
      rows.push({ head: 'At the wheel' });
      rows.push({ label: 'Motion ratio', value: num(mr, 3) });
      rows.push({ label: 'Wheel rate', value: num(wheelRate, 1) + ' lb/in' });
      rows.push({ label: 'Loss to leverage', value: num((1 - mr * mr) * 100, 1) + '%' });
      figures.push({ label: 'Wheel rate', value: num(wheelRate, 1) + ' lb/in',
                     sub: 'Spring rate times the motion ratio squared.' });
    }

    var status = { key: 'good', label: num(lbin, 0) + ' lb/in',
                   note: num(nmm, 2) + ' N/mm, ' + num(kgmm, 2) + ' kg/mm.' };
    var warnings = [];

    if (isFinite(input.corner) && input.corner > 0) {
      var staticDefl = input.corner / wheelRate;                  /* inches */
      var freqHz = (1 / (2 * Math.PI)) * Math.sqrt(G_IN_PER_S2 / staticDefl);
      rows.push({ head: 'On a ' + num(input.corner, 0) + ' lb corner' });
      rows.push({ label: 'Static deflection', value: num(staticDefl, 2) + ' in' });
      rows.push({ label: 'Ride frequency', value: num(freqHz, 2) + ' Hz' });
      rows.push({ label: 'Travel per 1g bump', value: num(staticDefl, 2) + ' in' });
      figures.push({ label: 'Ride frequency', value: num(freqHz, 2) + ' Hz',
                     sub: freqHz < 1.0 ? 'Soft. Comfortable road car territory.'
                        : (freqHz < 1.5 ? 'Firm road car or a mild street setup.'
                        : (freqHz < 2.0 ? 'Sport. Getting stiff for public roads.'
                                        : 'Race car stiff. Needs a smooth surface.')) });
      if (freqHz > 2.5) {
        warnings.push('Above about 2.5 Hz you are into dedicated track territory. On a public road that much rate makes the car slower, not faster, because the tires stop following the surface.');
      }
    }

    return { ok: true, errors: [], warnings: warnings, status: status,
             figures: figures, rows: rows };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "value",
          "key": "v",
          "type": "number"
      },
      {
          "name": "unit",
          "key": "u",
          "type": "select"
      },
      {
          "name": "motion",
          "key": "m",
          "type": "number"
      },
      {
          "name": "corner",
          "key": "cw",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-src__figure">'
        + '<div class="pc-src__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-src__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-src__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-src__row pc-src__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-src__row">'
        + '<span class="pc-src__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-src__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_SRC = { compute: compute };
})();
